import React, { useState } from 'react';
import { User } from '../types';
import { ApiService } from '../services/api';
import Register from './Register';
import { Key } from 'lucide-react';

interface AuthProps {
  onLogin: (user: User) => void;
  onBack?: () => void;
  initialView?: 'login' | 'register';
  intent?: 'login' | 'register' | 'buy';
}

const Auth: React.FC<AuthProps> = ({ onLogin, onBack, initialView = 'login', intent = 'login' }) => {
  const [showSplash, setShowSplash] = useState(true);
  const [view, setView] = useState<'login' | 'register' | 'forgot' | 'reset-code' | 'reset-password'>(initialView);
  const [loginLogoUrl, setLoginLogoUrl] = useState('https://skstreckenfliegen.blob.core.windows.net/mediadaten/logo.png');

  // Login View State
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Password Reset State
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setShowSplash(false);
    }, 2500);

    // Load loginLogoUrl from site assets
    ApiService.getSystemSettings().then(settings => {
      if (settings?.['site_assets']) {
        try {
          const parsed = JSON.parse(settings['site_assets']);
          if (parsed.loginLogoUrl) setLoginLogoUrl(parsed.loginLogoUrl);
        } catch { }
      }
    }).catch(() => { });

    // Listen for live updates from AdminMediaManager
    const handler = (e: Event) => {
      const assets = (e as CustomEvent).detail;
      if (assets?.loginLogoUrl) setLoginLogoUrl(assets.loginLogoUrl);
    };
    window.addEventListener('site-assets-update', handler);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('site-assets-update', handler);
    };
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const user = await ApiService.login(email, password);
      onLogin(user);
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Login fehlgeschlagen. Bitte Anmeldedaten prüfen.');
      setLoading(false);
    }
  };

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await ApiService.requestPasswordReset(resetEmail);
      setSuccess('Falls die E-Mail-Adresse existiert, wurde ein Code gesendet.');
      setView('reset-code');
    } catch (err: any) {
      setError(err.message || 'Anfrage fehlgeschlagen.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== newPasswordConfirm) {
      setError('Passwörter stimmen nicht überein.');
      return;
    }

    if (newPassword.length < 6) {
      setError('Passwort muss mindestens 6 Zeichen lang sein.');
      return;
    }

    setLoading(true);

    try {
      await ApiService.confirmPasswordReset({ email: resetEmail, code: resetCode, newPassword });
      setSuccess('Passwort erfolgreich geändert! Du kannst dich jetzt anmelden.');
      setTimeout(() => {
        setView('login');
        setSuccess('');
        setResetEmail('');
        setResetCode('');
        setNewPassword('');
        setNewPasswordConfirm('');
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Code ungültig oder abgelaufen.');
    } finally {
      setLoading(false);
    }
  };

  if (showSplash) {
    return (
      <div className="fixed inset-0 bg-slate-950 flex flex-col items-center justify-center z-[5000] animate-fadeIn">
        <div className="w-12 h-12 border-4 border-white/20 border-t-sky-500 rounded-full animate-spin shadow-2xl"></div>
      </div>
    );
  }

  if (view === 'register') {
    return (
      <div className="fixed inset-0 bg-slate-950 flex items-center justify-center p-6 z-[5000] overflow-y-auto">
        <div className="dark-glass max-w-2xl w-full p-8 rounded-2xl border border-white/10 shadow-2xl animate-fadeIn my-auto">
          <Register
            onCancel={() => setView('login')}
            onSuccess={(u) => {
              if (u) {
                onLogin(u);
              } else {
                setView('login');
              }
            }}
            intent={intent}
            onSwitchToLogin={() => setView('login')}
          />
        </div>
      </div>
    );
  }

  // Forgot Password: Step 1 - Enter Email
  if (view === 'forgot') {
    return (
      <div className="fixed inset-0 bg-slate-950 flex items-center justify-center p-6 z-[5000]">
        <div className="dark-glass max-w-md w-full p-8 rounded-2xl border border-white/10 shadow-2xl animate-fadeIn">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-sky-600/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl"><Key className="w-4 h-4 inline mr-1" /></span>
            </div>
            <h2 className="text-2xl font-black text-white tracking-tight uppercase">Passwort vergessen</h2>
            <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mt-2">Gib deine E-Mail-Adresse ein</p>
          </div>

          {error && <div className="bg-red-500/10 border border-red-500/20 text-red-400 text-xs py-2 px-4 rounded-xl mb-4 text-center font-bold uppercase">{error}</div>}
          {success && <div className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs py-2 px-4 rounded-xl mb-4 text-center font-bold uppercase">{success}</div>}

          <form onSubmit={handleRequestReset} className="space-y-4">
            <div>
              <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-1 ml-1">E-Mail</label>
              <input
                type="email" required value={resetEmail} onChange={(e) => setResetEmail(e.target.value)}
                className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-xl text-white text-base focus:ring-2 focus:ring-sky-600 outline-none transition-all"
                placeholder="pilot@example.com"
                autoComplete="email"
              />
            </div>

            <button
              type="submit" disabled={loading}
              className="w-full py-4 bg-sky-600 hover:bg-sky-700 disabled:opacity-50 text-white font-black rounded-xl shadow-lg transition-all active:scale-95 text-sm uppercase tracking-widest mt-4"
            >
              {loading ? 'Sende...' : 'Code anfordern'}
            </button>
          </form>

          <div className="text-center mt-6">
            <button onClick={() => { setView('login'); setError(''); setSuccess(''); }} className="text-slate-500 hover:text-white text-[10px] font-bold uppercase tracking-widest transition-colors">
              ← Zurück zum Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Forgot Password: Step 2 - Enter Code & New Password
  if (view === 'reset-code' || view === 'reset-password') {
    return (
      <div className="fixed inset-0 bg-slate-950 flex items-center justify-center p-6 z-[5000]">
        <div className="dark-glass max-w-md w-full p-8 rounded-2xl border border-white/10 shadow-2xl animate-fadeIn">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-emerald-600/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">✉️</span>
            </div>
            <h2 className="text-2xl font-black text-white tracking-tight uppercase">Code eingeben</h2>
            <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mt-2">
              Wir haben einen 6-stelligen Code an<br />
              <span className="text-sky-400">{resetEmail}</span> gesendet
            </p>
          </div>

          {error && <div className="bg-red-500/10 border border-red-500/20 text-red-400 text-xs py-2 px-4 rounded-xl mb-4 text-center font-bold uppercase">{error}</div>}
          {success && <div className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs py-2 px-4 rounded-xl mb-4 text-center font-bold uppercase">{success}</div>}

          <form onSubmit={handleConfirmReset} className="space-y-4">
            <div>
              <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-1 ml-1">6-Stelliger Code</label>
              <input
                type="text" required value={resetCode} onChange={(e) => setResetCode(e.target.value)}
                className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-xl text-white text-center text-2xl font-mono tracking-[0.5em] focus:ring-2 focus:ring-sky-600 outline-none transition-all"
                placeholder="000000"
                maxLength={6}
                pattern="[0-9]{6}"
                inputMode="numeric"
                autoComplete="one-time-code"
              />
            </div>

            <div>
              <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-1 ml-1">Neues Passwort</label>
              <input
                type="password" required value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-xl text-white text-base focus:ring-2 focus:ring-sky-600 outline-none transition-all"
                placeholder="••••••••"
                minLength={6}
                autoComplete="new-password"
              />
            </div>

            <div>
              <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-1 ml-1">Passwort bestätigen</label>
              <input
                type="password" required value={newPasswordConfirm} onChange={(e) => setNewPasswordConfirm(e.target.value)}
                className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-xl text-white text-base focus:ring-2 focus:ring-sky-600 outline-none transition-all"
                placeholder="••••••••"
                minLength={6}
                autoComplete="new-password"
              />
            </div>

            <button
              type="submit" disabled={loading}
              className="w-full py-4 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-black rounded-xl shadow-lg transition-all active:scale-95 text-sm uppercase tracking-widest mt-4"
            >
              {loading ? 'Speichere...' : 'Passwort ändern'}
            </button>
          </form>

          <div className="text-center mt-6 space-y-2">
            <button onClick={() => { setView('forgot'); setError(''); setSuccess(''); setResetCode(''); }} className="text-slate-500 hover:text-white text-[10px] font-bold uppercase tracking-widest transition-colors block mx-auto">
              Code erneut anfordern
            </button>
            <button onClick={() => { setView('login'); setError(''); setSuccess(''); }} className="text-slate-500 hover:text-white text-[10px] font-bold uppercase tracking-widest transition-colors block mx-auto">
              ← Zurück zum Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-slate-950 flex items-center justify-center p-6 z-[5000]">
      <div className="dark-glass max-w-md w-full p-8 rounded-2xl border border-white/10 shadow-2xl animate-fadeIn">
        <div className="text-center mb-8">
          <div className="w-56 h-56 bg-slate-900 rounded-3xl flex items-center justify-center border border-white/10 mx-auto mb-6 shadow-xl">
            <img src={loginLogoUrl} alt="Streckenfliegen App Logo" className="w-40 h-40 object-contain" />
          </div>
          <h2 className="text-4xl font-black text-white tracking-tight uppercase">Login</h2>
          <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mt-2">Bitte anmelden zur Fortsetzung</p>
        </div>

        {error && <div className="bg-red-500/10 border border-red-500/20 text-red-400 text-xs py-2 px-4 rounded-xl mb-4 text-center font-bold uppercase">{error}</div>}

        <style>{`
          input:-webkit-autofill,
          input:-webkit-autofill:hover, 
          input:-webkit-autofill:focus, 
          input:-webkit-autofill:active{
              -webkit-box-shadow: 0 0 0 30px #0f172a inset !important;
              -webkit-text-fill-color: white !important;
              transition: background-color 5000s ease-in-out 0s;
          }
        `}</style>

        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-1 ml-1">E-Mail</label>
            <input
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-xl text-white text-base focus:ring-2 focus:ring-sky-600 outline-none transition-all"
              placeholder="pilot@roth.ag"
              autoComplete="username"
            />
          </div>
          <div>
            <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-1 ml-1">Passwort</label>
            <input
              type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-xl text-white text-base focus:ring-2 focus:ring-sky-600 outline-none transition-all"
              placeholder="••••••••"
              autoComplete="current-password"
            />
          </div>

          <div className="text-right">
            <button
              type="button"
              onClick={() => { setView('forgot'); setError(''); setResetEmail(email); }}
              className="text-sky-500 hover:text-sky-400 text-[11px] font-bold uppercase tracking-widest transition-colors"
            >
              Passwort vergessen?
            </button>
          </div>

          <button
            type="submit" disabled={loading}
            className="w-full py-4 bg-sky-600 hover:bg-sky-700 disabled:opacity-50 text-white font-black rounded-xl shadow-lg transition-all active:scale-95 text-sm uppercase tracking-widest mt-4"
          >
            {loading ? 'Lade...' : 'Anmelden'}
          </button>
        </form>

        <div className="text-center mt-6 space-y-4">
          <button 
            onClick={() => setView('register')} 
            className="w-full py-3 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-xl transition-all border border-white/5 hover:border-white/20 text-xs uppercase tracking-widest"
          >
            Noch kein Konto? Registrieren
          </button>
          
          {onBack && (
            <button onClick={onBack} className="text-slate-500 hover:text-white text-[10px] font-bold uppercase tracking-widest transition-colors block mx-auto">
              ← Zurück zur Startseite
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Auth;
